import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import useLocationGraphConfig, { ComputeScaleExtremes } from '../components/hooks/useLocationGraphConfig';
import useHotkeyHandlers from '../components/hooks/useHotkeyHandlers';
import DataViewSwitch from '../components/data_view_switch';
import UnitSnapshotReport from '../components/unit_snapshot_report';
import UnitSnapshotGraph from '../components/unit_snapshot_graph';
import { formatDateTime } from '../components/enums';

// Material UI
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import IconButton from '@mui/material/IconButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';


// Unit Snapshot sub-page rendering (Location Name, Title, String Selection, Measurement Selection)
export default function UnitSnapshot() {
	const { locationNum } = useParams();
	
	// State Management
	const [selectedString, setSelectedString] = useState(0);
	
	const [measurementList, setMeasurementList] = useState([]);
	const [selectedMeasIndex, setSelectedMeasIndex] = useState(Number(0));
	const [measArrowDisabled, setMeasArrowDisabled] = useState({
		back: true,
		forward: true,
	});
	
	const [snapshotData, setSnapshotData] = useState({});
	const { locationInfo, limitData, graphScalesData, isLoading, setIsLoading } = useLocationGraphConfig({
		locationNum,
		limitsEndpoint: `/loc/${locationNum}/limits_flat/unit`,
		scalesEndpoint: `/loc/${locationNum}/graph_scales/unit`
	});
	const graphScalesExtremes = ComputeScaleExtremes(graphScalesData);

	const [graphDisplay, setGraphDisplay] = useState(false);
	const [graphType, setGraphType] = useState(0);
	
	const selectedMeasIndexRef = useRef();
	const graphTypeRef = useRef();

	// Helpers to check if snapshotData is empty and if DataViewSwitch should be disabled
	const isSnapshotDataEmpty = !snapshotData || (Object.keys(snapshotData).length === 0);
	const isDataSwitchDisabled = isLoading || isSnapshotDataEmpty;
	const dataSwitchDisabledText = isSnapshotDataEmpty ? "No data available to graph" : "Data is loading...";
	
	// Handle the current selectedMeasIndex for hotkey nav
	useEffect(() => {
		selectedMeasIndexRef.current = selectedMeasIndex;
	}, [selectedMeasIndex]);

	// Handle the current graphType for hotkey nav
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	// Handle the enabling/disabling of the measurement navigation buttons
	useEffect(() => {
		if (measurementList !== null && measurementList.length > 0) {
			setMeasArrowDisabled({
				back: selectedMeasIndex === Number(measurementList.length - 1),
				forward: selectedMeasIndex === 0,
			});
		}
	}, [selectedMeasIndex]);

	// Handle keypresses for measurement and graph navigation
	useHotkeyHandlers([
		{
			key: 'PageUp',
			down: (e) => {
				e.preventDefault();  // Prevent default scrolling behavior
				if (measurementList.length !== 0 && selectedMeasIndexRef.current !== (measurementList.length - 1)) {
					setSelectedMeasIndex(Number(selectedMeasIndexRef.current + 1));
				}
			},
			up: () => {},
		},
		{
			key: 'PageDown',
			down: (e) => {
				e.preventDefault();
				if (measurementList.length !== 0 && selectedMeasIndexRef.current !== 0) {
					setSelectedMeasIndex(Number(selectedMeasIndexRef.current - 1));
				}
			},
			up: () => {},
		},
		{
			key: 'F8',
			down: (e) => {
				e.preventDefault();
				switch(graphTypeRef.current) {
					case 0:  // Voltage -> Impedance
						setGraphType(1);
						break;
					case 1:  // Impedance -> Temperature
						setGraphType(2);
						break;
					case 2:  // Temperature -> Voltage
						setGraphType(0);
						break;
					case 3:  // Diff Impedance -> Impedance
						setGraphType(1);
						break;
					default:
						setGraphType(0);
						break;
				}
			},
			up: () => {},
		},
		{
			key: 'F9',
			down: (e) => {
				e.preventDefault();
				if (graphTypeRef.current === 1) {  // Impedance -> Diff Impedance
					setGraphType(3);
				} else if (graphTypeRef.current === 3) {  // Diff Impedance -> Impedance
					setGraphType(1);
				}
			},
			up: () => {},
		},
	]);
	
	// Get the list of measurement ids and datetimes for the specified location id
	useEffect(() => {
		setIsLoading(true);

		axiosInstance.get(`/loc/${locationNum}/snapshot/`)
			.then(response => {
				const measurementList = response.data;
				setMeasurementList(measurementList);
				
				if (measurementList.length !== 0) {
					setSelectedMeasIndex(Number(0));
					setMeasArrowDisabled({forward: true, back: false});
				}
			})
			.catch(error => {
				console.error("Error fetching measurement list data:", error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [locationNum]);
	
	// Get all snapshot data for the specified measurement id
	useEffect(() => {
		if (measurementList.length && selectedMeasIndex >= 0) {
			setIsLoading(true);

			let measurement_id = measurementList[Number(selectedMeasIndex)].measurement_id;
			axiosInstance.get(`/loc/${locationNum}/snapshot/${measurement_id}`)
				.then(response => {
					setSnapshotData(response.data);
				})
				.catch(error => {
					console.error("Error fetching snapshot data:", error);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [locationNum, selectedMeasIndex, measurementList]);

		
	// Event Handlers
	const handleGraphTypeChange = (event) => {
		setGraphType(Number(event.target.value));
	}
	
	const handleStringChange = (event) => {
		setSelectedString(Number(event.target.value));
	}
	
	const handleMeasurementChange = (event) => {
		setSelectedMeasIndex(Number(event.target.value));
	}
	
	const handleMeasurementChangeBack = () => {
		if (measurementList.length !== 0 && selectedMeasIndex !== (measurementList.length - 1)) {
			setSelectedMeasIndex(Number(selectedMeasIndex + 1));
		}
	}
	
	const handleMeasurementChangeForward = () => {
		if (measurementList.length !== 0 && selectedMeasIndex !== 0) {
			setSelectedMeasIndex(Number(selectedMeasIndex - 1));
		}
	}
	
	const handleViewChange = (event) => {
		setGraphDisplay(event.target.checked);
	}
		
	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.location_name } (SN {locationInfo.serial_number})
			</Typography>
			<Toolbar>
				<Grid container spacing={1} alignItems="center">
					<Grid item xs alignItems="left">
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							Unit Snapshot
						</Typography>
					</Grid>
					{graphDisplay &&
						<Grid item>
							<FormControl sx={{ m: 1, minWidth: 100 }}>
								<label id="graph-type-select" style={{fontFamily:"Saira, sans-serif"}}>Graph Type</label>
								<select
									id="graph-type-select"
									value={graphType}
									label="Graph Type"
									onChange={handleGraphTypeChange}
								>
									<option key={0} value={0}>Voltage</option>
									<option key={1} value={1}>Impedance</option>
									<option key={3} value={3}>Diff. Impedance</option>
									<option key={2} value={2}>Temperature</option>
								</select>
							</FormControl>
						</Grid>
					}
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 80 }}>
							<label id="string-select" style={{fontFamily:"Saira, sans-serif"}}>String</label>
							<select
								id="string-select"
								value={selectedString}
								label="String"
								onChange={handleStringChange}
								sx= {{ maxHeight: 500 }}
								>
								<option key={0} value={0}>All Strings</option>
								{ Array.from({length: locationInfo.num_strings}, (_, i) => i + 1).map((stringNum) => (
									<option key={stringNum} value={stringNum}>String {stringNum}</option>
								)) }
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<IconButton
							variant="contained"
							onClick={handleMeasurementChangeBack}
							disabled={measArrowDisabled.back}
							sx={{
								ml: 1,
								mr: 0,
								pl: 2,
								height: 25,
								width: 25,
								borderRadius: '5px',
								border: '1px solid',
								align: 'center',
								bgcolor: '#B1282E',
								'&:hover': {
									bgcolor: "#B1282E77"
								},
							}}
						>
							<ArrowBackIosIcon sx={{ fontSize: 15, color: "#FFFFFF" }} align='center' />
						</IconButton>
					</Grid>
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 160 }}>
							<label id="measurement-select" style={{fontFamily:"Saira, sans-serif"}}>Measurement</label>
							<select
								id="measurement-select"
								value={selectedMeasIndex}
								label="Measurement"
								onChange={handleMeasurementChange}
								sx= {{ maxHeight: 500 }}
							>
								{measurementList.map((measurement, index) => (
									<option
										key={measurement.measurement_id}
										value={index}
									>
										{formatDateTime(measurement.datetime)}
									</option>
								))}
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<IconButton
							variant='contained'
							onClick={handleMeasurementChangeForward}
							disabled={measArrowDisabled.forward}
							sx={{
								ml: 0,
								mr: 1,
								pl: 1,
								height: 25,
								width: 25,
								borderRadius: '5px',
								border: '1px solid',
								align: 'center',
								bgcolor: '#B1282E',
								'&:hover': {
									bgcolor: "#B1282E77"
								},
							}}
						>
							<ArrowForwardIosIcon sx={{ fontSize: 15, color: "#FFFFFF" }} align='center' />
						</IconButton>
					</Grid>
					<Grid item>
						<DataViewSwitch
							checked={graphDisplay}
							onChange={handleViewChange}
							leftText="Report"
							rightText="Graph"
							disabled={isDataSwitchDisabled}
							disabledText={dataSwitchDisabledText}
						/>
					</Grid>
				</Grid>
			</Toolbar>
			{ graphDisplay ?
				<UnitSnapshotGraph
					snapshotData={snapshotData}
					limitData={limitData}
					graphScalesExtremes={graphScalesExtremes}
					selectedString={selectedString}
					numUnits={locationInfo.num_units_per_string}
					graphType={graphType}
					loading={isLoading}
				/>
			:
				<UnitSnapshotReport
					snapshotData={snapshotData}
					selectedString={selectedString}
					loading={isLoading}
				/>
			}
		</Paper>
	);
}